import { Component, OnInit } from '@angular/core';
import { TalentService } from '../talent.service';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MatDialog } from '@angular/material/dialog';
import { SalesDetailsDialogComponent } from './sales-details-dialog.component';

interface SalesData {
  account_manager_id: number;
  account_manager_name: string;
  new_roles_count: number;
  resume_submitted: number;
  num_interviews: number;
  num_selected: number;
  num_started: number;
  roles_list?: any[];
  resume_submitted_details?: any[];
  interviews_details?: any[];
  selected_details?: any[];
  started_details?: any[];
}

@Component({
  selector: 'app-sales-analytics',
  template: `
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <h2 class="page-title">Sales Analytics</h2>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-6">
          <div class="row">
            <div class="col-md-5">
              <mat-form-field appearance="fill">
                <mat-label>Start Date</mat-label>
                <input matInput [matDatepicker]="startPicker" [formControl]="dateRange.controls.start">
                <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
              </mat-form-field>
            </div>
            <div class="col-md-5">
              <mat-form-field appearance="fill">
                <mat-label>End Date</mat-label>
                <input matInput [matDatepicker]="endPicker" [formControl]="dateRange.controls.end">
                <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
              </mat-form-field>
            </div>
            <div class="col-md-2">
              <button mat-raised-button color="primary" (click)="onDateChange()" style="margin-top: 5px;">
                <mat-icon>search</mat-icon> Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div *ngIf="isLoading" class="loading-overlay">
            <mat-spinner></mat-spinner>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h3>New Roles Summary</h3>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Account Manager</th>
                      <th>New Roles</th>
                      <th>Resume Submitted</th>
                      <th>First Interview</th>
                      <th>Selected</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let data of salesData">
                      <td>{{data.account_manager_name}}</td>
                      <td>
                        <a href="javascript:void(0)" (click)="openDetailsDialog(data.account_manager_id, data.account_manager_name)" class="clickable-count">
                          {{data.new_roles_count}}
                        </a>
                      </td>
                      <td>
                        <a href="javascript:void(0)" (click)="openResumeSubmittedDialog(data.account_manager_id, data.account_manager_name, data.resume_submitted_details)" class="clickable-count">
                          {{data.resume_submitted}}
                        </a>
                      </td>
                      <td>
                        <a href="javascript:void(0)" (click)="openFirstInterviewDialog(data.account_manager_id, data.account_manager_name, data.interviews_details)" class="clickable-count">
                          {{data.num_interviews}}
                        </a>
                      </td>
                      <td>
                        <a href="javascript:void(0)" (click)="openSelectedDialog(data.account_manager_id, data.account_manager_name, data.selected_details)" class="clickable-count">
                          {{data.num_selected}}
                        </a>
                      </td>
                      <td>
                        <a href="javascript:void(0)" (click)="openStartedDialog(data.account_manager_id, data.account_manager_name, data.started_details)" class="clickable-count">
                          {{data.num_started}}
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="row mt-3">
        <div class="col-12">
          <div class="text-right" style="display: flex; justify-content: flex-end;">
            <button mat-raised-button color="accent" (click)="downloadExcel()">
              <mat-icon>download</mat-icon> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.7);
      z-index: 1000;
    }
    
    .clickable-count {
      color: #007bff;
      text-decoration: none;
      cursor: pointer;
    }
    
    .clickable-count:hover {
      text-decoration: underline;
      color: #0056b3;
    }
  `]
})
export class SalesAnalyticsComponent implements OnInit {
  // Date range
  dateRange = new FormGroup({
    start: new FormControl(),
    end: new FormControl()
  });

  // Data
  salesData: SalesData[] = [];
  isLoading = true;

  constructor(
    private talentService: TalentService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.setupInitialState();
  }

  setupInitialState() {
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.dateRange.setValue({
      start: thirtyDaysAgo,
      end: today
    });

    // Load data on initialization
    this.loadData();
  }

  loadData() {
    this.isLoading = true;

    const startDate = this.dateRange.value.start ?
      moment(this.dateRange.value.start).format('YYYY-MM-DD') :
      moment().subtract(30, 'days').format('YYYY-MM-DD');

    const endDate = this.dateRange.value.end ?
      moment(this.dateRange.value.end).format('YYYY-MM-DD') :
      moment().format('YYYY-MM-DD');

    console.log('Fetching sales analytics data with date range:', { startDate, endDate });

    this.talentService.getSalesAnalytics(startDate, endDate).subscribe(
      data => {
        console.log('Received sales analytics data:', data);
        this.salesData = data || [];
        this.isLoading = false;
      },
      error => {
        console.error('Error fetching sales analytics data:', error);
        this.salesData = [];
        this.isLoading = false;
      }
    );
  }

  onDateChange() {
    console.log('Date range changed');
    this.loadData();
  }

  openDetailsDialog(accountManagerId: number, accountManagerName: string, detailType: string = 'roles', detailData: any[] | null = null) {
    console.log(`Opening ${detailType} details dialog for account manager: ${accountManagerName} (ID: ${accountManagerId})`);
    
    const startDate = this.dateRange.value.start ?
      moment(this.dateRange.value.start).format('YYYY-MM-DD') :
      moment().subtract(30, 'days').format('YYYY-MM-DD');

    const endDate = this.dateRange.value.end ?
      moment(this.dateRange.value.end).format('YYYY-MM-DD') :
      moment().format('YYYY-MM-DD');
    
    this.isLoading = true;
    
    // Convert accountManagerId to string as the API expects a string
    const accountManagerIdStr = String(accountManagerId);
    console.log(`Using account manager ID: ${accountManagerIdStr}`);
    
    console.log(`Making API call with accountManagerId=${accountManagerIdStr}, startDate=${startDate}, endDate=${endDate}`);
    
    // If detail data is already provided, use it directly
    if (detailData) {
      this.isLoading = false;
      
      console.log(`Opening dialog with ${detailType} data:`, detailData);
      
      this.dialog.open(SalesDetailsDialogComponent, {
        width: '80%',
        data: {
          account_manager: accountManagerName,
          roles: detailData,
          start_date: startDate,
          end_date: endDate,
          detail_type: detailType
        }
      });
      return;
    }
    
    // Otherwise fetch the data from the API
    this.talentService.getSalesAnalyticsDetails(accountManagerIdStr, startDate, endDate).subscribe(
      data => {
        console.log('Received sales analytics details:', data);
        console.log('Data type:', typeof data);
        console.log('Is array:', Array.isArray(data));
        console.log('Length:', data ? (Array.isArray(data) ? data.length : 'not an array') : 'null or undefined');
        
        this.isLoading = false;
        
        // Ensure data is an array
        const rolesData = Array.isArray(data) ? data : [];
        
        console.log('Opening dialog with roles data:', rolesData);
        
        this.dialog.open(SalesDetailsDialogComponent, {
          width: '80%',
          data: {
            account_manager: accountManagerName,
            roles: rolesData,
            start_date: startDate,
            end_date: endDate,
            detail_type: detailType
          }
        });
      },
      error => {
        console.error('Error fetching sales analytics details:', error);
        this.isLoading = false;
        
        // Show dialog with error message
        this.dialog.open(SalesDetailsDialogComponent, {
          width: '80%',
          data: {
            account_manager: accountManagerName,
            roles: [],
            start_date: startDate,
            end_date: endDate,
            detail_type: detailType,
            error: `Error fetching data: ${error.message || 'Unknown error'}`
          }
        });
      }
    );
  }

  openResumeSubmittedDialog(accountManagerId: number, accountManagerName: string, detailData: any[]) {
    console.log('Resume Submitted Detail Data:', detailData);
    this.openDetailsDialog(accountManagerId, accountManagerName, 'resume_submitted', detailData);
  }
  
  openFirstInterviewDialog(accountManagerId: number, accountManagerName: string, detailData: any[]) {
    console.log('First Interview Detail Data:', detailData);
    this.openDetailsDialog(accountManagerId, accountManagerName, 'first_interview', detailData);
  }
  
  openSelectedDialog(accountManagerId: number, accountManagerName: string, detailData: any[]) {
    console.log('Selected Detail Data:', detailData);
    this.openDetailsDialog(accountManagerId, accountManagerName, 'selected', detailData);
  }
  
  openStartedDialog(accountManagerId: number, accountManagerName: string, detailData: any[]) {
    console.log('Started Detail Data:', detailData);
    this.openDetailsDialog(accountManagerId, accountManagerName, 'started', detailData);
  }
  
  downloadExcel() {
    console.log('Downloading Excel file');
    
    // Create data for Excel export
    const excelData: any[][] = [];
    
    // Add header row
    const headerRow = ['Account Manager', 'New Roles', 'Resume Submitted', 'First Interview', 'Selected', 'Started'];
    excelData.push(headerRow);
    
    // Add data rows
    this.salesData.forEach(data => {
      excelData.push([
        data.account_manager_name, 
        data.new_roles_count, 
        data.resume_submitted,
        data.num_interviews,
        data.num_selected,
        data.num_started
      ]);
    });
    
    // Create worksheet
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Account Manager column
      { wch: 15 }, // New Roles column
      { wch: 20 }, // Resume Submitted column
      { wch: 15 }, // First Interview column
      { wch: 15 }, // Selected column
      { wch: 15 }  // Started column
    ];
    ws['!cols'] = colWidths;
    
    // Create workbook
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Analytics');
    
    // Generate Excel file
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Save file
    const startDate = this.dateRange.value.start ? 
      moment(this.dateRange.value.start).format('YYYY-MM-DD') : 
      moment().subtract(30, 'days').format('YYYY-MM-DD');
    
    const endDate = this.dateRange.value.end ? 
      moment(this.dateRange.value.end).format('YYYY-MM-DD') : 
      moment().format('YYYY-MM-DD');
    
    const fileName = `Sales_Analytics_${startDate}_to_${endDate}.xlsx`;
    const data: Blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, fileName);
  }
}
